import { networkManager, WonderlandWebsocketEvent } from '@wonderlandcloud/client';
import { Events } from './network-events.js';
import { IntifaceRPCNode } from './IntifaceRPCNode.js';
import { ButtplugClientDevice } from 'buttplug';
import { DeviceRemoteCommand, IntifaceController } from './IntifaceController.js';
import { EventsClient } from './events-client.js';
import { Emitter } from '@wonderlandengine/api';
import { MenuController, Submenu } from './menus/menu-controller.js';
import Logger from './utils/logger.js';
import { ConnectionError, ICEError, RPCError } from './utils/intiface-errors.js';

const MODULE = 'RemoteRec';

/**
 * IntifaceRemoteRec
 * Receiver side of an RPC relationship over RTCPeerConnection
 */
export class IntifaceRemoteRec extends IntifaceRPCNode {
    static TypeName = 'IntifaceRemoteRec';
    /** Default timeout duration (in milliseconds) for receiving device control offers */
    private static readonly DEFAULT_OFFER_TIMEOUT_MS = 5000;
    /** Prevent aggressive repeated restartIce() calls from dual state handlers. */
    private static readonly ICE_RESTART_COOLDOWN_MS = 3000;

    /** Numeric peer ID of the currently connected controller (-1 if none) */
    private peerId: number = -1;
    /** Temporary key used by controllers to identify this receiver during negotiation */
    private _listenKey: string = "";
    /** Flag to prevent ICE restart attempts during intentional disconnection */
    private disconnecting: boolean = false;
    /** Timestamp of last ICE restart attempt to enforce cooldown. */
    private lastIceRestartAt: number = 0;
    /** Reference to network event handler for cleanup */
    private onEventHandler: ((e: any) => void) | null = null;

    /**
     * Gets the temporary key that controllers use to identify this receiver.
     * @returns The listen key string
     */
    get listenKey(): string {
        return this._listenKey;
    }

    /**
     * Gets the peer ID of the controller currently controlling this receiver.
     * @returns Numeric peer ID, or -1 if no controller connected
     */
    get controllingPeerId(): number {
        return this.peerId;
    }

    constructor() {
        super();
    }

    /**
     * Registers with the server to receive device control offers from controllers.
     * Listens for incoming WebRTC offers and ICE candidates.
     * @param timeoutMs - Timeout duration for waiting for server response (default: DEFAULT_OFFER_TIMEOUT_MS)
     * @returns Promise resolving to the listen key that controllers should use to connect
     * @throws {Error} If server doesn't respond within timeout period
     */
    async listenForDeviceControlOffers(timeoutMs: number = IntifaceRemoteRec.DEFAULT_OFFER_TIMEOUT_MS): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            let timeoutHandle: number | null = null;

            const onEvent = (e: WonderlandWebsocketEvent) => {
                try {
                    switch (e.type) {
                        case Events.PLAYER_REQUESTING_DEVICECONTROL_OFFER:
                            Logger.debug(`Received offer from sender: ${e.data.id}`, MODULE);

                            this.handleOffer(e.data.id, e.data.description);

                            break;
                        case Events.PLAYER_REQUESTING_DEVICECONTROL_OFFER_ICE:
                            Logger.debug(`Received ICE candidate from sender: ${e.data.id}`, MODULE);

                            if (e.data && e.data.candidate) {
                                if (this.intifacePC?.remoteDescription) {
                                    this.intifacePC?.addIceCandidate(e.data.candidate)
                                        .then(() => Logger.debug('Added ICE candidate', MODULE))
                                        .catch((err) => {
                                            const iceError = new ICEError('Failed to add ICE candidate', e.data.candidate);
                                            Logger.error('Could not add ICE candidate', MODULE, iceError);
                                        });
                                }
                                else {
                                    // Buffer candidate for later, but enforce max size to prevent memory bloat
                                    if (this.remoteCandidates.length < IntifaceRPCNode.MAX_REMOTE_CANDIDATES) {
                                        this.remoteCandidates.push(e.data.candidate);
                                    } else {
                                        Logger.warn(`ICE candidate buffer full (${IntifaceRPCNode.MAX_REMOTE_CANDIDATES}), discarding candidate. Connection may be failing.`, MODULE);
                                    }
                                }
                            }

                            break;
                        case Events.PLAYER_PERMITTING_DEVICECONTROL_SUCCESS:
                            Logger.info(`Listening with key: ${e.data.data}`, MODULE);
                            cleanup();
                            this._listenKey = e.data.data;
                            resolve(this.listenKey);
                            break;
                    }
                } catch (err) {
                    // Malformed event messages are logged but don't interrupt the connection flow
                    Logger.debug('Ignoring malformed network event during listen', MODULE, err);
                }
            }

            const cleanup = () => {
                if (timeoutHandle !== null) {
                    clearTimeout(timeoutHandle);
                }
                // Don't remove event listener here - it needs to stay active to receive offers
            };

            // Timeout
            timeoutHandle = window.setTimeout(() => {
                cleanup();
                // Only remove listener on timeout/error
                if (this.onEventHandler) {
                    networkManager.onEvent.remove(this.onEventHandler);
                    this.onEventHandler = null;
                }
                reject(new Error("Timed out waiting for response from server."));
            }, timeoutMs);

            // Register RPC methods for each connection to ensure proper binding to data channel
            this.registerMethod("sendDeviceCommand", this.sendDeviceCommands.bind(this));
            this.registerMethod("stopDeviceCommand", this.stopDeviceCommand.bind(this));
            this.registerMethod("requestDeviceSync", this.requestDeviceSync.bind(this));
            
            // Remove old event listener if exists, then add new one
            if (this.onEventHandler) {
                networkManager.onEvent.remove(this.onEventHandler);
            }
            this.onEventHandler = onEvent;
            networkManager.onEvent.add(onEvent);

            networkManager.client?.sendViaWs({
                type: Events.PLAYER_PERMITTING_DEVICECONTROL,
                data: '',
            });
        });
    }

    /**
     * RPC method called by controller to execute a device command.
     * Notifies controller of state changes and handles timed command completion callbacks.
     * @param remoteCommand - Device command details including index, type, values, and duration
     */
    private async sendDeviceCommands(remoteCommand: DeviceRemoteCommand) {
        Logger.debug(`Executing device command for device ${remoteCommand.deviceIndex}`, MODULE);
        // Inform controller that a new command has been applied (optimistic update)
        try {
            this.call("updateDeviceCommandState", { deviceIndex: remoteCommand.deviceIndex, command: remoteCommand });
        } catch (err) {
            Logger.error('Could not notify controller of device command state', MODULE, err);
            // Re-throw so caller knows the notification failed
            throw new RPCError('Failed to notify controller', 'updateDeviceCommandState', remoteCommand);
        }

        // IntifaceController.sendDeviceCommand now handles timeouts internally.
        // If a timed command, provide callback to notify controller when it completes.
        const onComplete = (remoteCommand.duration && !isNaN(parseFloat(remoteCommand.duration)) && parseFloat(remoteCommand.duration) > 0)
            ? () => {
                try {
                    this.call('updateDeviceCommandState', { deviceIndex: remoteCommand.deviceIndex, command: null });
                } catch (err) {
                    // Log but don't re-throw from callback - command already completed
                    const rpcError = new RPCError('Failed to notify controller of command completion', 'updateDeviceCommandState');
                    Logger.error('Could not notify controller of device command completion', MODULE, rpcError);
                }
            }
            : undefined;

        await IntifaceController.instance.sendDeviceCommand(
            remoteCommand.deviceIndex,
            remoteCommand.actuateType,
            remoteCommand.actuateVals,
            remoteCommand.duration,
            remoteCommand.modVals,
            onComplete
        );
    }

    /**
     * RPC method called by controller to stop a device.
     * Clears any active timeouts and notifies controller of state change.
     * @param payload - Object containing deviceIndex
     */
    private async stopDeviceCommand(payload: any) {
        const deviceIndex: number = payload?.deviceIndex;
        if (deviceIndex === undefined) return;
        // Use IntifaceController.stopDevice to properly clear timeouts and stop device
        await IntifaceController.instance.stopDevice(deviceIndex);
        // Notify controller that device was stopped
        try {
            this.call('updateDeviceCommandState', { deviceIndex, command: null });
        } catch (err) {
            Logger.warn('Failed to notify controller of stop', MODULE, err);
        }
    }

    /**
     * Handles incoming WebRTC offer from a controller.
     * Creates peer connection and initiates answer process.
     * @param senderId - Numeric peer ID of the controller
     * @param offer - WebRTC offer description
     */
    async handleOffer(senderId: number, offer: any) {
        // Refresh TURN servers before creating peer connection (in case credentials expired while listening)
        await EventsClient.instance?.loadTurnIceServers?.().catch((err) =>
            Logger.warn('Failed to load TURN servers for device control receiver offer', MODULE, err)
        );
        this.createRecPeerConnection(senderId);
        Logger.debug(`Setting peerId to ${senderId}`, MODULE);
        this.peerId = senderId;

        await this.acceptOffer(senderId, offer);
    }

    /**
     * Disconnects from the controller and cleans up resources.
     * Removes event listeners, closes data channel and peer connection.
     */
    public async disconnect() {
        Logger.info('Disconnecting', MODULE);
        this.disconnecting = true;
        
        // Remove network event listener
        if (this.onEventHandler) {
            Logger.debug('Removing network event listener', MODULE);
            networkManager.onEvent.remove(this.onEventHandler);
            this.onEventHandler = null;
        }
        
        // Explicitly close data channel and peer connection
        if (this.intifaceDC) {
            Logger.debug('Closing data channel', MODULE);
            try {
                this.intifaceDC.close();
            } catch (err) {
                Logger.warn('Error closing data channel', MODULE, err);
            }
        }
        if (this.intifacePC) {
            Logger.debug('Closing peer connection', MODULE);
            try {
                this.intifacePC.close();
            } catch (err) {
                Logger.warn('Error closing peer connection', MODULE, err);
            }
        }
        
        await this.close();
        this.peerId = -1;
        this.disconnecting = false;
    }

    /**
     * Accepts a WebRTC offer by setting remote description and creating an answer.
     * Sends the answer back to the controller via server.
     * @param senderId - Numeric peer ID of the controller
     * @param offer - WebRTC offer description to accept
     */
    async acceptOffer(senderId: number, offer: any) {
        Logger.debug('Accepting offer and creating answer', MODULE);

        const offersdp = JSON.parse(JSON.stringify(offer));

        let peerConnection = this.intifacePC;

        await peerConnection?.setRemoteDescription(offersdp)
            .then(() => peerConnection?.createAnswer())
            .then(async answer => {
                await peerConnection?.setLocalDescription(answer);
                Logger.debug('Sending answer to controller', MODULE);

                        //Message through server channel
                        networkManager.client?.sendViaWs({
                            type: Events.PLAYER_REQUESTING_DEVICECONTROL_ANSWER,
                            data: {
                                answer: answer,
                                receiverId: this.peerId
                            },
                        });
            });
    }

    /**
     * Sends the current device list and states to the controller via RPC.
     * Called when devices are added/removed or when controller first connects.
     * @param devices - Map of local Buttplug devices to serialize and send
     */
    public updateDevices(devices: Map<number, ButtplugClientDevice>) {
        let json = IntifaceController.instance.jsonifyDevices(devices);
        Logger.debug(`Sending device list update (${devices.size} devices)`, MODULE);
        this.call("updateDevices", { devicesJson: json, peerId: this.peerId });
    }

    /**
     * RPC method called by the controller to request the current device snapshot.
     * Returns serialized device data instead of pushing it immediately, which avoids
     * timing issues on slower mobile browsers.
     */
    private requestDeviceSync() {
        return {
            devicesJson: IntifaceController.instance.jsonifyDevices(IntifaceController.instance.getLocalDevices()),
            peerId: this.peerId,
        };
    }

    /**
     * Creates and configures the RTCPeerConnection for receiver-to-controller communication.
     * Sets up ICE handling, signaling state management, and connection lifecycle callbacks.
     * @param senderId - Numeric peer ID of the controller
     * @returns Configured RTCPeerConnection instance
     */
    private async createRecPeerConnection(senderId: number) {
        const iceServers: RTCIceServer[] = [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:3478' },
            { urls: 'stun:stun2.l.google.com:19302' },
            { urls: 'stun:stun3.l.google.com:3478' },
            { urls: 'stun:stun4.l.google.com:19302' },
            { urls: 'stun:stun4.l.google.com:3478' },
        ];

        // Add TURN servers if available
        const turnServers = EventsClient.instance?.getTurnIceServers?.();
        if (turnServers?.length) {
            iceServers.push(...turnServers);
        }

        let vrpc = new RTCPeerConnection({
            iceServers,
        });

        vrpc.onicegatheringstatechange = (event) => {
            Logger.debug('ICE gathering state change', 
            MODULE,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            event.target.iceGatheringState);
        };
        vrpc.onicecandidate = (event) => {
            if (event.candidate) {
                networkManager.client?.sendViaWs({
                    type: Events.PLAYER_REQUESTING_DEVICECONTROL_ANSWER_ICE,
                    data: {
                        receiverId: this.peerId,
                        candidate: event.candidate,
                    },
                });
            }
        };
        vrpc.onicecandidateerror = (event) => {
            Logger.warn('RemoteRec: ICE candidate error', MODULE, event);
        };
        vrpc.onsignalingstatechange = () => {
            Logger.debug(`Signaling state: ${vrpc?.signalingState}`, MODULE);
            if (vrpc &&
                (vrpc.signalingState === 'have-remote-offer' ||
                    vrpc.signalingState === 'stable')) {
                this.remoteCandidates.forEach((can) => {
                    vrpc?.addIceCandidate(can)
                        .then(() => Logger.debug('Added cached ICE candidate', MODULE))
                        .catch((err) => {
                            const iceError = new ICEError('Failed to add cached ICE candidate', can);
                            Logger.error('Could not add cached ICE candidate', MODULE, iceError);
                        });
                });
                this.remoteCandidates = [];
            }
        };
        vrpc.onconnectionstatechange = () => {
            Logger.debug(`Connection state: ${vrpc.connectionState}`, MODULE);

            if (vrpc.connectionState === 'failed') {
                this.requestIceRestart(vrpc, `connection:${vrpc.connectionState}`);
            }
            if (vrpc?.connectionState === 'failed') {
                Logger.error('Failed to establish RTCPeerConnection', MODULE);
                void this.logSelectedCandidatePair(vrpc, 'receiver');
            }
            
            // When connection is successfully established, update UI
            if (vrpc.connectionState === 'connected') {
                Logger.info(`Connection established with peer ${this.peerId}`, MODULE);
                MenuController.instance.updateConditionals();
                MenuController.instance.updateDeviceControls();
            }
            
            // When connection is fully closed, update UI and clear active controller ID.
            if (vrpc.connectionState === 'closed') {
                Logger.info('Connection closed', MODULE);
                this.peerId = -1;
                // Update UI to reflect disconnection
                MenuController.instance.updateConditionals();
                MenuController.instance.updateDeviceControls();
            }
        };
        vrpc.oniceconnectionstatechange = () => {
            Logger.debug(`ICE connection state: ${vrpc.iceConnectionState}`, MODULE);
            if (vrpc.iceConnectionState === 'failed' && !this.disconnecting) {
                this.requestIceRestart(vrpc, `ice:${vrpc.iceConnectionState}`);
            }
        };
        // If remote created a datachannel (callee path), we'll capture it here
        vrpc.ondatachannel = (ev) => {
            Logger.debug('Data channel created by remote controller', MODULE);
            this.intifaceDC = ev.channel;
            this.setupDataChannel(this.intifaceDC);
        };

        this.intifacePC = vrpc;

        return vrpc;
    }

    /** Log selected ICE candidate pair details to diagnose connectivity failures. */
    private async logSelectedCandidatePair(pc: RTCPeerConnection, role: string): Promise<void> {
        try {
            const stats = await pc.getStats();
            let selectedPair: any = null;
            const candidatePairs: any[] = [];
            const candidates = new Map<string, any>();
            let selectedPairId: string | undefined;
            const localByType: Record<string, number> = {};
            const remoteByType: Record<string, number> = {};

            stats.forEach((report: any) => {
                if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
                    candidates.set(report.id, report);
                    const bucket = report.type === 'local-candidate' ? localByType : remoteByType;
                    const key = `${report.candidateType ?? 'unknown'}/${report.protocol ?? 'n/a'}`;
                    bucket[key] = (bucket[key] ?? 0) + 1;
                }
                if (report.type === 'candidate-pair') {
                    candidatePairs.push(report);
                    if (report.selected || report.nominated || report.state === 'succeeded') {
                        selectedPair = report;
                    }
                }
                if (report.type === 'transport' && report.selectedCandidatePairId) {
                    selectedPairId = report.selectedCandidatePairId;
                }
            });

            if (!selectedPair && selectedPairId) {
                selectedPair = candidatePairs.find((pair: any) => pair.id === selectedPairId) ?? null;
            }

            if (!selectedPair && candidatePairs.length > 0) {
                selectedPair = candidatePairs[0];
            }

            if (!selectedPair) {
                const localSummary = Object.entries(localByType).map(([k, v]) => `${k}:${v}`).join(', ') || 'none';
                const remoteSummary = Object.entries(remoteByType).map(([k, v]) => `${k}:${v}`).join(', ') || 'none';
                Logger.warn(
                    `No ICE candidate pair stats available for ${role}; local=[${localSummary}] remote=[${remoteSummary}]`,
                    MODULE
                );
                return;
            }

            const local = candidates.get(selectedPair.localCandidateId);
            const remote = candidates.get(selectedPair.remoteCandidateId);
            Logger.warn(
                `ICE pair (${role}) state=${selectedPair.state} nominated=${selectedPair.nominated} ` +
                `local=${local?.candidateType ?? 'unknown'}(${local?.protocol ?? 'n/a'}) ` +
                `remote=${remote?.candidateType ?? 'unknown'}(${remote?.protocol ?? 'n/a'})`,
                MODULE
            );
        } catch (err) {
            Logger.warn(`Failed to inspect ICE stats for ${role}`, MODULE, err);
        }
    }

    /** Attempt ICE restart with cooldown to avoid rapid restart loops on unstable networks. */
    private requestIceRestart(pc: RTCPeerConnection, reason: string): void {
        if (this.disconnecting) {
            return;
        }

        const now = Date.now();
        if (now - this.lastIceRestartAt < IntifaceRemoteRec.ICE_RESTART_COOLDOWN_MS) {
            Logger.warn(`Skipping ICE restart (${reason}) due to cooldown`, MODULE);
            return;
        }

        this.lastIceRestartAt = now;
        Logger.warn(`Attempting ICE restart (${reason})`, MODULE);
        pc.restartIce();
    }
}
